import { FormEvent, useRef, useState } from "react"
import {Copy} from 'lucide-react'
import toast from "react-hot-toast"
import { BANK, SITE } from "@/data/site"
import { lumi } from "@/lib/lumi"

const inputClass =
  "border-b border-white/15 bg-transparent pb-2 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 focus:border-[#c4a574]"

function Order() {
  const formRef = useRef<HTMLFormElement>(null)
  const [qty, setQty] = useState(0)
  const [copied, setCopied] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const orderTotal = qty * SITE.price

  const copyValue = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(label)
      window.setTimeout(() => setCopied(""), 1600)
    } catch {
      toast.error("複製失敗，請手動複製")
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (qty < 1) {
      toast.error("請選擇購買數量")
      return
    }

    const form = event.currentTarget
    const formData = new FormData(form)
    const name = String(formData.get("name") || "")
    const phone = String(formData.get("phone") || "")
    const email = String(formData.get("email") || "")
    const transferDate = String(formData.get("transferDate") || "")
    const transferAmount = String(formData.get("transferAmount") || "")
    const accountLastFive = String(formData.get("accountLastFive") || "")
    const receiverName = String(formData.get("receiverName") || "")
    const receiverPhone = String(formData.get("receiverPhone") || "")
    const storeName = String(formData.get("storeName") || "")
    const storeAddress = String(formData.get("storeAddress") || "")
    const note = String(formData.get("note") || "")

    const now = new Date().toISOString()
    const transferAmountNumber = Number(transferAmount) || 0

    setSubmitting(true)
    try {
      await lumi.entities.orders.create({
        name,
        phone,
        email,
        product: SITE.productName,
        edition: SITE.edition,
        quantity: qty,
        price: SITE.price,
        amount: orderTotal,
        transferDate,
        transferAmount: transferAmountNumber,
        accountLastFive,
        receiverName,
        receiverPhone,
        storeName,
        storeAddress,
        note,
        status: "pending",
        creator: email || name,
        createdAt: now,
        updatedAt: now,
      })
      form.reset()
      setQty(0)
      toast.success("送出成功")
    } catch (error) {
      console.error("訂單送出失敗：", error)
      toast.error("送出失敗，請稍後再試")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section id="order" className="relative w-full bg-[#0c0c0c] px-8 py-28 md:px-16 md:py-40 lg:px-24">
      <p className="font-sans-ui text-center text-[11px] tracking-[0.48em] text-[#c4a574]">Order</p>
      <h2 className="font-display mt-6 text-center text-4xl text-[#f7f1e6] md:text-6xl">立即訂購</h2>
      <p className="font-serif-tc mx-auto mt-6 w-full whitespace-nowrap text-center text-[11px] leading-7 text-stone-400 md:text-sm md:leading-8">
        請完成匯款後填寫以下資料，我們將依照付款順序安排出貨。
      </p>
      <div className="mx-auto mt-16 max-w-2xl">
        <form ref={formRef} className="border-t border-white/10 pt-10" onSubmit={handleSubmit}>
          <h3 className="font-sans-ui text-center text-sm tracking-[0.32em] text-[#c4a574]">訂購人資料</h3>
          <div className="mt-10 grid gap-10 md:grid-cols-2">
            <label className="flex flex-col gap-3">
              <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">姓名</span>
              <input required type="text" name="name" className={inputClass} />
            </label>
            <label className="flex flex-col gap-3">
              <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">手機號碼</span>
              <input required type="tel" name="phone" className={inputClass} />
            </label>
            <label className="flex flex-col gap-3 md:col-span-2">
              <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">Email</span>
              <input required type="email" name="email" className={inputClass} />
            </label>
          </div>

          <h3 className="font-sans-ui mt-20 text-center text-sm tracking-[0.32em] text-[#c4a574]">購買商品數量</h3>
          <div className="mt-10 flex items-center justify-between gap-6 border-b border-white/10 pb-8">
            <div>
              <p className="font-display text-2xl text-[#f7f1e6]">{SITE.productName}</p>
              <p className="font-sans-ui mt-1 whitespace-nowrap text-[10px] tracking-[0.08em] text-stone-500 md:text-sm md:tracking-[0.18em]">
                {SITE.edition} · {SITE.priceLabel}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center border border-white/20 font-sans-ui text-stone-300 transition-colors duration-300 hover:border-[#c4a574] hover:text-[#c4a574]"
                aria-label="減少數量"
                onClick={() => setQty((prev) => Math.max(0, prev - 1))}
              >
                −
              </button>
              <span className="font-display w-8 text-center text-xl text-[#f7f1e6]">{qty}</span>
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center border border-white/20 font-sans-ui text-stone-300 transition-colors duration-300 hover:border-[#c4a574] hover:text-[#c4a574]"
                aria-label="增加數量"
                onClick={() => setQty((prev) => prev + 1)}
              >
                +
              </button>
            </div>
          </div>
          <p className="font-sans-ui mt-6 w-full text-[10px] leading-5 tracking-normal text-stone-500 md:text-right md:text-sm md:leading-6 md:tracking-[0.18em]">
            ※ 售價 NT$1,580 已含 7-ELEVEN 店到店運費，無須另加。
          </p>

          <h3 className="font-sans-ui mt-20 text-center text-sm tracking-[0.32em] text-[#c4a574]">匯款資訊</h3>
          <div className="mt-10 divide-y divide-white/10 border border-white/10 font-serif-tc text-sm text-stone-300">
            {BANK.map((item) => (
              <div key={item.label} className="flex items-center justify-between gap-4 px-6 py-6">
                <div className="flex flex-col gap-1.5">
                  <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-400">{item.label}</span>
                  <span className="font-serif-tc text-lg tracking-wide text-[#f7f1e6]">{item.value}</span>
                </div>
                <button
                  type="button"
                  aria-label={`複製${item.label}`}
                  className="flex h-10 w-10 shrink-0 items-center justify-center border border-white/15 text-stone-400 transition-colors duration-300 hover:border-[#c4a574] hover:text-[#c4a574]"
                  onClick={() => copyValue(item.value, item.label)}
                >
                  <Copy className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
          {copied ? (
            <p className="font-sans-ui mt-3 text-center text-[11px] tracking-[0.18em] text-[#c4a574]">
              已複製{copied}
            </p>
          ) : null}
          <p className="font-sans-ui mt-4 text-center text-[11px] leading-6 tracking-[0.08em] text-stone-500 md:text-sm md:leading-7">
            售價已含運費，匯款金額請依訂購組數計算。
            <br />
            請先完成匯款後再填寫以下資料。
          </p>

          <h3 className="font-sans-ui mt-20 text-center text-sm tracking-[0.32em] text-[#c4a574]">匯款確認</h3>
          <div className="mt-10 grid gap-10 md:grid-cols-2">
            <label className="flex flex-col gap-3">
              <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">匯款日期</span>
              <input required type="date" name="transferDate" className={inputClass} />
            </label>
            <label className="flex flex-col gap-3">
              <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">匯款金額</span>
              <input required type="number" min="0" name="transferAmount" className={inputClass} />
            </label>
            <label className="flex flex-col gap-3 md:col-span-2">
              <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">匯款帳號末五碼</span>
              <input required type="text" maxLength={5} name="accountLastFive" className={inputClass} />
            </label>
          </div>

          <h3 className="font-sans-ui mt-20 text-center text-sm tracking-[0.32em] text-[#c4a574]">7-ELEVEN 店到店資訊</h3>
          <div className="mt-10 grid gap-10 md:grid-cols-2">
            <label className="flex flex-col gap-3">
              <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">收件人姓名</span>
              <input required type="text" name="receiverName" className={inputClass} />
            </label>
            <label className="flex flex-col gap-3">
              <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">收件人手機</span>
              <input required type="tel" name="receiverPhone" className={inputClass} />
            </label>
            <label className="flex flex-col gap-3">
              <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">7-ELEVEN 門市名稱</span>
              <input required type="text" name="storeName" className={inputClass} />
            </label>
            <label className="flex flex-col gap-3">
              <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-300">7-ELEVEN 門市地址</span>
              <input required type="text" name="storeAddress" className={inputClass} />
            </label>
          </div>

          <h3 className="font-sans-ui mt-20 text-center text-sm tracking-[0.32em] text-[#c4a574]">備註</h3>
          <p className="font-serif-tc mx-auto mt-6 max-w-md text-center text-sm leading-7 text-stone-400">
            訂單送出後，將寄送 Email 通知，
            <br />
            確認我們已收到您的訂單資訊。
            <br />
            完成付款確認後，
            <br />
            將依訂單順序安排出貨。
          </p>
          <label className="mt-8 flex flex-col gap-2">
            <span className="font-sans-ui text-sm tracking-[0.22em] text-stone-400">其他備註（選填）</span>
            <textarea rows={3} name="note" className={`resize-none ${inputClass}`} />
          </label>

          <div className="mt-10 flex flex-col items-center gap-4 border-t border-white/10 pt-10">
            <div className="font-sans-ui flex items-center gap-3 text-sm tracking-[0.28em] text-stone-500">
              <span>訂購金額</span>
              <span className="font-display text-xl text-[#c4a574]">NT$ {orderTotal.toLocaleString("zh-TW")}</span>
            </div>
            <button
              type="submit"
              disabled={qty < 1 || submitting}
              className="font-sans-ui mt-2 border border-[#c4a574]/60 px-10 py-4 text-sm tracking-[0.32em] text-[#c4a574] transition-colors duration-500 hover:bg-[#c4a574] hover:text-[#080808] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#c4a574]"
            >
              {submitting ? "送出中" : "確認送出訂單"}
            </button>
            <p className="font-sans-ui text-[11px] tracking-[0.18em] text-stone-600">送出前請確認所有資料正確無誤</p>
          </div>
        </form>
      </div>
    </section>
  )
}

export default Order
